/* ──────────────────────────────────────────────────────────────
 *  useGeometry — TanStack Query hook for 3-D geometry mesh data
 *
 *  Fetches tessellated mesh from the backend keyed by:
 *    [sampleId, bushingType, viewType]
 *
 *  Config/input values (materials, weight pockets, core ID, etc.)
 *  are read from a frozen snapshot taken at compute time so that
 *  editing inputs after compute does NOT change the displayed
 *  geometry. The snapshot is updated only when compute runs again.
 *
 *  Performance optimizations for high-frequency selection changes:
 *    • AbortSignal forwarding — cancels in-flight request when key changes
 *    • placeholderData: keepPreviousData — shows last mesh during transitions
 *    • structuralSharing: false — skip deep-equality on large vertex arrays
 *    • Bounded gcTime — prevents memory bloat with many cached meshes
 *    • enabled guard — prevents fire on null/stale state
 *
 *  EXPORT-GATING FIX:
 *   This hook is the OTHER place (besides useComputeExecution's initial
 *   geometry loop) where a sample's /mesh call can succeed — specifically
 *   when the user switches to an already-generated sample that hasn't had
 *   its geometry fetched yet. When that fetch succeeds here, we mark
 *   (computeJobId, sampleId) as geometry-ready in useJobStore so that
 *   useExportJobs's trigger effect — which gates on that same readiness
 *   map — knows it's now safe to fire export for this sample. Without
 *   this, only samples generated during the initial compute loop would
 *   ever unlock export; every subsequent sample switch would silently
 *   never trigger it.
 * ────────────────────────────────────────────────────────────── */

import { useQuery } from '@tanstack/react-query';
import { getGeometry } from '@/api/geometryApi';
import { useBushingStore } from '@/stores/useBushingStore';
import { useSampleStore } from '@/stores/useSampleStore';
import { useJobStore } from '@/stores/useJobStore';
import type { GeometryMesh, GeometryView } from '@/types/geometry';

/** Query key factory for geometry. */
export const geometryKeys = {
  all: ['geometry'] as const,
  mesh: (
    jobId: string,
    sampleId: string,
    bushingType: string,
    viewType: GeometryView,
    minArea: number,
    coreMaterial: string,
    coreId: number | null,
    sleeveThickness: number,
  ) =>
    ['geometry', 'mesh', jobId, sampleId, bushingType, viewType, minArea, coreMaterial, coreId, sleeveThickness] as const,
};

interface UseGeometryOptions {
  viewType: GeometryView;
  /** External enable/disable override. When false, query is always disabled. */
  prefetchEnabled?: boolean;
}

export interface UseGeometryResult {
  mesh: GeometryMesh | undefined;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  error: unknown;
}

export function useGeometry({ viewType, prefetchEnabled }: UseGeometryOptions): UseGeometryResult {
  const currentSelectedId = useSampleStore((s) => s.currentSelectedId);
  const genRowsById = useSampleStore((s) => s.genRowsById);
  const bushingType = useBushingStore((s) => s.bushingType);
  const computeJobId = useJobStore((s) => s.computeJobId);

  // Read config/input values from the frozen snapshot taken at compute time.
  // This ensures editing inputs after compute does NOT trigger geometry updates.
  const snapshot = useSampleStore((s) => s.geometryConfigSnapshot);

  const coreMaterial = snapshot?.coreMaterial ?? 'Aluminum';
  const outerMaterial = snapshot?.outerMaterial ?? 'Aluminum';
  const interSleeveMaterial = snapshot?.interSleeveMaterial ?? 'Aluminum';
  const weightPockets = snapshot?.weightPockets ?? true;
  const minContactArea = snapshot?.minContactArea ?? null;
  const coreId = snapshot?.coreId ?? null;
  const sleeveThickness = snapshot?.sleeveThickness ?? 0;
  const calculatedMinContactArea = snapshot?.calculatedMinContactArea ?? null;
  const linearTravelRadialPlus = snapshot?.linearTravelRadialPlus ?? null;
  const linearTravelRadialMinus = snapshot?.linearTravelRadialMinus ?? null;

  // Effective min_area: explicit config value > calculated value > 0
  const effectiveMinArea = (weightPockets && coreMaterial !== 'Steel')
    ? (minContactArea ?? calculatedMinContactArea ?? 0)
    : 0;

  const isGeneratedSample = Boolean(
    currentSelectedId &&
    currentSelectedId.startsWith('N') &&
    genRowsById[currentSelectedId],
  );

  const rawSampleData = isGeneratedSample && currentSelectedId
    ? (genRowsById[currentSelectedId] as Record<string, number>)
    : null;

  // For D1: inject travA/travB = linear_travel_radial + diameter_swaging/2
  // This mirrors the legacy logic in create_geometry_d1.
  const sampleData = (() => {
    if (!rawSampleData) return null;
    if (bushingType !== 'D1') return rawSampleData;
    const diameterSwaging = Number(
      rawSampleData['DIAMETER_SWAGING'] ?? rawSampleData['diameter_swaging'] ?? 0,
    );
    const travA = (linearTravelRadialPlus ?? 2.0) + diameterSwaging / 2;
    const travB = (linearTravelRadialMinus ?? 2.0) + diameterSwaging / 2;
    return { ...rawSampleData, travA, travB };
  })();

  const { data, isLoading, isFetching, isError, error } = useQuery<GeometryMesh>({
    queryKey: geometryKeys.mesh(
      computeJobId ?? '',
      currentSelectedId ?? '',
      bushingType,
      viewType,
      effectiveMinArea,
      coreMaterial,
      coreId,
      sleeveThickness,
    ),
    // Pass AbortSignal so in-flight requests are cancelled on key change
    queryFn: async ({ signal }) => {
      try {
        const mesh = await getGeometry(
          {
            jobId: computeJobId!,
            sampleId: currentSelectedId!,
            bushingType,
            viewType,
            sampleData: sampleData!,
            coreMaterial,
            outerMaterial,
            interSleeveMaterial,
            coreId,
            minArea: effectiveMinArea,
            weightPockets,
            sleeveThickness,
          },
          { signal },
        );

        // ── Mark this (jobId, sampleId) pair geometry-ready ──────────
        // This fetch succeeding means the backend's /mesh endpoint has
        // synchronously stored job_state (bush parameters) for this
        // exact pair before returning — see geometry_service.py. That's
        // the frontend's optimistic signal that export MAY be safe to
        // trigger for this sample; useExportJobs watches this same map
        // and gates its trigger effect on it. The backend /jobs/export
        // endpoint independently re-validates job_state completeness
        // before actually starting generation, so this flag is a UX
        // gate only, not the source of truth.
        if (computeJobId && currentSelectedId) {
          useJobStore.getState().markGeometryReady(computeJobId, currentSelectedId);
        }

        return mesh;
      } catch (err: unknown) {
        // Re-throw abort/cancel as a native error so TanStack Query
        // recognises it as a cancellation (not a real failure).
        if (signal?.aborted) {
          throw new DOMException('Aborted', 'AbortError');
        }
        throw err;
      }
    },
    enabled: Boolean(computeJobId && currentSelectedId && isGeneratedSample && sampleData && snapshot) && prefetchEnabled !== false,
    staleTime: 30 * 60 * 1000, // 30 min — geometry rarely changes for same sample
    gcTime: 10 * 60 * 1000, // 10 min GC — prevents memory bloat with many samples
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    // Don't retry on 404 (endpoint may not exist yet); retry once on other errors
    retry: (failureCount, err) => {
      const status = (err as { status?: number })?.status;
      if (status === 404) return false;
      return failureCount < 1;
    },
    // Skip expensive deep-equality on large vertex/triangle arrays
    structuralSharing: false,
  });

  return {
    mesh: data,
    isLoading,
    isFetching,
    isError,
    error,
  };
}